import { getRandomNumber } from "@/utils/utils";
import { AlertTitle } from "@mui/material";
import MuiAlert, { AlertColor, AlertProps } from "@mui/material/Alert";
import Slide from "@mui/material/Slide";
import Snackbar, { SnackbarOrigin } from "@mui/material/Snackbar";
import { TransitionProps } from "@mui/material/transitions";
import React, {
  createContext,
  ReactElement,
  useCallback,
  useContext,
  useState,
} from "react";

// SnackbarProvider and context for global MUI Snackbar management.
// Usage: Wrap your app with <SnackbarProvider>. Use the useSnackbar hook in any child to trigger a Snackbar.
// Supports: message, type, icon, duration, action, position, onClose, variant.
// Example:
//   const showSnackbar = useSnackbar();
//   showSnackbar({ message: 'Saved!', type: 'success' });

type SnackbarOptions = {
  message: string;
  type?: AlertColor;
  icon?: React.ReactNode;
  duration?: number;
  action?: React.ReactNode;
  position?: SnackbarOrigin;
  onClose?: () => void;
  variant?: AlertProps["variant"];
  traceId?: string;
};

interface SnackbarContextType {
  showSnackbar: (options: SnackbarOptions) => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(
  undefined
);

const MAX_STACK = 3;

const ERROR_SNACKBAR_STYLES = {
  backgroundColor: "#f44336",
  color: "#ffffff",
  "& .MuiAlert-icon": {
    color: "#ffffff",
  },
};

const getAlertStyles = (type?: AlertColor) => {
  return type === "error" ? ERROR_SNACKBAR_STYLES : undefined;
};

function SlideTransition(
  props: TransitionProps & { children: ReactElement<any, any> }
) {
  return <Slide {...props} direction="up" />;
}

export const SnackbarProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [snackStack, setSnackStack] = useState<
    (SnackbarOptions & { key: number })[]
  >([]);

  const showSnackbar = useCallback((opts: SnackbarOptions) => {
    setSnackStack((prev) => {
      const next = [...prev, { ...opts, key: Date.now() + getRandomNumber() }];
      // Only keep the last MAX_STACK snackbars
      return next.slice(-MAX_STACK);
    });
  }, []);

  const handleClose = (key: number, reason?: string) => {
    if (reason === "clickaway") return;
    setSnackStack((prev) => prev.filter((snack) => snack.key !== key));
    const closedSnack = snackStack.find((snack) => snack.key === key);
    closedSnack?.onClose?.();
  };

  // Determine anchorOrigin for all snackbars (use the most recent or default)
  const anchorOrigin: SnackbarOrigin =
    snackStack.length > 0
      ? (snackStack[snackStack.length - 1].position ?? {
          vertical: "top",
          horizontal: "right",
        })
      : { vertical: "top", horizontal: "right" };

  // Helper to get stacking style based on anchorOrigin
  const getStackOffset = (idx: number) => {
    const offset = idx * 72; // 72px per snackbar (height + margin)
    if (
      anchorOrigin.vertical === "bottom" &&
      anchorOrigin.horizontal === "right"
    ) {
      return { bottom: `${offset}px`, right: 0 };
    } else if (
      anchorOrigin.vertical === "bottom" &&
      anchorOrigin.horizontal === "left"
    ) {
      return { bottom: `${offset}px`, left: 0 };
    } else if (
      anchorOrigin.vertical === "bottom" &&
      anchorOrigin.horizontal === "center"
    ) {
      return { bottom: `${offset}px`, left: 0, right: 0, margin: "0 auto" };
    } else if (
      anchorOrigin.vertical === "top" &&
      anchorOrigin.horizontal === "right"
    ) {
      return { top: `${offset}px`, right: 0 };
    } else if (
      anchorOrigin.vertical === "top" &&
      anchorOrigin.horizontal === "left"
    ) {
      return { top: `${offset}px`, left: 0 };
    } else if (
      anchorOrigin.vertical === "top" &&
      anchorOrigin.horizontal === "center"
    ) {
      return { top: `${offset}px`, left: 0, right: 0, margin: "0 auto" };
    }
    return {};
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      {snackStack.map((snack, idx) => (
        <Snackbar
          key={snack.key}
          open
          /**
           *@desc default snackBar show Time - 2.8 sec
           */
          autoHideDuration={snack.duration === 0 ? null : 2800}
          onClose={(_, reason) => handleClose(snack.key, reason)}
          anchorOrigin={anchorOrigin}
          action={snack.action}
          sx={{
            zIndex: 1400,
            position: "fixed",
            ...getStackOffset(idx),
          }}
          slots={{
            transition: SlideTransition,
          }}
        >
          <MuiAlert
            elevation={6}
            variant={snack.variant ?? "filled"}
            onClose={
              snack.duration === 0 ? undefined : () => handleClose(snack.key)
            }
            severity={snack.type ?? "info"}
            icon={snack.icon}
            sx={getAlertStyles(snack.type)}
          >
            <AlertTitle>{snack.message}</AlertTitle>
            {snack.traceId &&
              (snack.traceId ? `Trace ID: ${snack.traceId}` : "")}
          </MuiAlert>
        </Snackbar>
      ))}
    </SnackbarContext.Provider>
  );
};

export const useSnackbarContext = () => {
  const ctx = useContext(SnackbarContext);
  if (!ctx)
    throw new Error(
      "useSnackbarContext must be used within a SnackbarProvider"
    );
  return ctx;
};
